// panels/CreateItemModal.jsx
import React, { useState, useCallback, useEffect } from 'react';
import { manufacturingAPI } from '../../../services/api';
import './CreateItemModal.css';

const ITEM_TYPES = [
  { value: 'raw_material', label: 'Raw Material' },
  { value: 'manufactured', label: 'Manufactured Part' },
  { value: 'assembly', label: 'Assembly' },
  { value: 'finished_good', label: 'Finished Good' },
];

const CreateItemModal = ({ onClose, onCreate, existingPendingCodes = [] }) => {
  const [itemCode, setItemCode] = useState('');
  const [itemName, setItemName] = useState('');
  const [description, setDescription] = useState('');
  const [itemType, setItemType] = useState('manufactured');
  const [isPhantom, setIsPhantom] = useState(false);
  
  const [isValidating, setIsValidating] = useState(false);
  const [codeError, setCodeError] = useState('');
  const [codeValid, setCodeValid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Debounced validation of item code
  useEffect(() => {
    if (!itemCode.trim()) {
      setCodeError('');
      setCodeValid(false);
      return;
    }

    // Check against pending items first (instant)
    const codeUpper = itemCode.trim().toUpperCase();
    if (existingPendingCodes.some(c => c.toUpperCase() === codeUpper)) {
      setCodeError('This code is already being created on the canvas');
      setCodeValid(false);
      return;
    }

    // Debounce API check
    const timer = setTimeout(async () => {
      setIsValidating(true);
      try {
        const response = await manufacturingAPI.items.list({
          search: itemCode.trim(),
          page_size: 10,
        });
        
        const items = response.data.results || response.data;
        const exactMatch = items.find(
          item => item.item_code.toUpperCase() === codeUpper
        );
        
        if (exactMatch) {
          setCodeError(`Item code "${exactMatch.item_code}" already exists`);
          setCodeValid(false);
        } else {
          setCodeError('');
          setCodeValid(true);
        }
      } catch (err) {
        console.error('Failed to validate item code:', err);
        setCodeError('');
        setCodeValid(true); // Allow on error, will validate on save
      } finally {
        setIsValidating(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [itemCode, existingPendingCodes]);

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    
    if (!itemCode.trim() || !itemName.trim()) {
      return;
    }

    if (codeError || !codeValid) {
      return;
    }

    setIsSubmitting(true);

    // Create the pending item object
    const newItem = {
      id: `pending-${Date.now()}`, // Temporary ID
      item_code: itemCode.trim().toUpperCase(),
      name: itemName.trim(),
      description: description.trim(),
      item_type: itemType,
      is_phantom: isPhantom,
      isPending: true, // Mark as not yet saved
    };

    onCreate(newItem);
    onClose();
  }, [itemCode, itemName, description, itemType, isPhantom, codeError, codeValid, onCreate, onClose]);

  const isFormValid = itemCode.trim() && itemName.trim() && codeValid && !codeError && !isValidating;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="create-item-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <h2>Create New Item</h2>
          <button className="close-btn" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div className="modal-content">
            {/* Info Banner */}
            <div className="info-banner">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="16" x2="12" y2="12"/>
                <line x1="12" y1="8" x2="12.01" y2="8"/>
              </svg>
              <span>This item will be saved when you click "Save Changes" on the main page. You can edit it on the canvas before saving.</span>
            </div>

            {/* Item Code */}
            <div className="form-group">
              <label htmlFor="itemCode">
                Item Code <span className="required">*</span>
              </label>
              <div className="input-with-status">
                <input
                  id="itemCode"
                  type="text"
                  value={itemCode}
                  onChange={(e) => setItemCode(e.target.value.toUpperCase())}
                  placeholder="e.g. PART-001"
                  autoFocus
                  className={codeError ? 'error' : codeValid ? 'valid' : ''}
                />
                <div className="input-status">
                  {isValidating && (
                    <span className="status-validating">
                      <span className="mini-spinner"></span>
                    </span>
                  )}
                  {!isValidating && codeValid && (
                    <span className="status-valid">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                    </span>
                  )}
                  {!isValidating && codeError && (
                    <span className="status-error">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="18" y1="6" x2="6" y2="18"/>
                        <line x1="6" y1="6" x2="18" y2="18"/>
                      </svg>
                    </span>
                  )}
                </div>
              </div>
              {codeError && <p className="field-error">{codeError}</p>}
              {!codeError && codeValid && (
                <p className="field-success">Item code is available</p>
              )}
            </div>

            {/* Item Name */}
            <div className="form-group">
              <label htmlFor="itemName">
                Item Name <span className="required">*</span>
              </label>
              <input
                id="itemName"
                type="text"
                value={itemName}
                onChange={(e) => setItemName(e.target.value)}
                placeholder="e.g. Steel Bracket Assembly"
              />
            </div>

            {/* Description */}
            <div className="form-group">
              <label htmlFor="description">
                Description
              </label>
              <textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Optional description for this item"
                rows={3}
              />
            </div>

            {/* Item Type */}
            <div className="form-group">
              <label htmlFor="itemType">Item Type</label>
              <select
                id="itemType"
                value={itemType}
                onChange={(e) => setItemType(e.target.value)}
              >
                {ITEM_TYPES.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Is Phantom */}
            <div className="form-group checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={isPhantom}
                  onChange={(e) => setIsPhantom(e.target.checked)}
                />
                <span className="checkbox-text">
                  Phantom Item
                  <span className="checkbox-hint">
                    Components explode through to parent assembly
                  </span>
                </span>
              </label>
            </div>
          </div>

          {/* Footer */}
          <div className="modal-footer">
            <button type="button" className="btn-cancel" onClick={onClose}>
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn-create"
              disabled={!isFormValid || isSubmitting}
            >
              {isSubmitting ? 'Creating...' : 'Add to Canvas'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateItemModal;